import React, { useEffect, useState } from 'react';
import { VendorUserType } from 'auth/VendorAuth/vendorUserSlice';
import axios from 'axios';
import ElementContainer, { ReadOnlyElementContainer } from 'components/elements/ElementContainer';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { filter, sortBy } from 'lodash';
import { showErrorMessage } from 'store/errorModalSlice';
import { saveElementValuesAsync, selectRfi } from 'store/rfiSlice';
import { selectVendorResponse, submitResponse } from 'store/vendorResponseSlice';
import RfiElement from 'types/RfiElement';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { fieldTypes, RfiStatus, SectionGroup, sectionNames } from '../../types/RfiTypes';
import { vendorNavPages } from './VendorNav';

interface IRfiVendorAllProps {
  user: VendorUserType;
  section: string;
  setSelectedPage: (page: number) => void;
  readOnly?: boolean;
  reset: () => void;
}

const VendorRfiValidation = (props: IRfiVendorAllProps) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  // Constants
  let attestation_text =
    "I attest that all data entered in this response to <RFI number> is correct and valid.  I am submitting this response on behalf of <Vendor Legal Business Name>.";
  let submission_text =
    "You have successfully submitted your response to <RFI Number> at <date and time>";

  // Hooks
  const rfi = useAppSelector(selectRfi);
  const dispatch = useAppDispatch();
  const vendorResponse = useAppSelector(selectVendorResponse);

  // State
  const timeZoneElement = rfi.finalize?.find((e: RfiElement) => {
    return e.mapsToField === "RESPONSES_DUE_TZ";
  });
  const timeZone =
    timeZoneElement &&
    timeZoneElement.answers &&
    timeZoneElement.answers[0]?.value
      ? timeZoneElement.answers[0]?.value
      : undefined;

  //State
  const [validationPass, setValidationPass] = useState(false);
  const [validationFail, setValidationFail] = useState(true);
  const [requiredVendorInfo, setRequiredVendorInfo] = useState<RfiElement[]>(
    []
  );
  const [requiredQuestions, setRequiredQuestions] = useState<RfiElement[]>([]);
  const [checked, setChecked] = React.useState(false);
  const [submissionDate, setSubmissionDate] = useState<string>("");
  const [responseDueDatePassed, setResponseDueDatePassed] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const getVendorInfoElementByMapToName = (name: string) => {
    return rfi.vendorInfo?.find((e: RfiElement) => {
      return e.mapsToField === name;
    });
  };

  let VENDOR_NAME = getVendorInfoElementByMapToName("VENDOR_LEGAL_NAME");

  // Handlers
  const downloadRfiResponsePdf = () => {
    setIsPrinting(true);
    axios
      .get(
        `/api/v1/rfi/print/${rfi.rfi?.rfiId}/response/${vendorResponse.vendorResponse?.vendorResponseId}`,
        { responseType: "blob" }
      )
      .then((response) => {
        let url = URL.createObjectURL(response.data);
        let a = document.createElement("a");
        a.href = url;
        a.download = `Response_${rfi.rfi?.rfiNumber}_${rfi.rfi?.versionNumber}.pdf`;
        a.rel = "noopener";
        a.click();
        setIsPrinting(false);
      });
  };

  const handleChange = (event: CheckboxChangeEvent) => {
    setChecked(!checked);
  };

  const onValidate = () => {
    dispatch(saveElementValuesAsync());
    props.reset();
  };

  const submitVendorResponse = async (
    rfiId: number,
    vendorResponseId: number
  ) => {
    return axios.put(
      `/api/v1/rfi/${rfiId}/vendorresponses/${vendorResponseId}/submit`
    );
  };

  const clickSubmit = () => {
    if (checked) {
      if (rfi.rfi?.rfiId) {
        submitVendorResponse(rfi.rfi?.rfiId, rfi.rfi?.vendorResponseId)
          .then(() => {
            setSubmissionDate(
              dayjs
                .utc()
                .tz(timeZone ?? "America/New_York")
                .format("lll z")
            );
            dispatch(submitResponse());
          })
          .catch((error) => {
            console.log(error);
            dispatch(showErrorMessage(`Error Submitting Response.`));
          });
      }
    }
  };

  // Effects
  //set the vendor info elements to validate
  useEffect(() => {
    //Check to see rfi and vendor info are valid objects
    if (rfi && rfi.vendorInfo) {
      const unansweredRequiredElements = filter(
        rfi.vendorInfo,
        (element: any) => {
          return (
            element.requiredResponse === true &&
            !(
              element.fieldType === fieldTypes.SECTIONHEADER ||
              element.fieldType === fieldTypes.GUIDANCETEXT
            ) &&
            (!element.answers ||
              element.answers.length <= 0 ||
              !element.answers[0].value)
          );
        }
      );

      var sortedUnansweredRequiredElements = sortBy(
        unansweredRequiredElements,
        (element: any) => {
          return element.sections.find(
            (x: SectionGroup) => x.sectionGroupName === props.section
          )?.sequence;
        }
      );

      setRequiredVendorInfo(sortedUnansweredRequiredElements);
    }
  }, [rfi.rfi]);

  //set the vendor questionnaire elements to validate
  useEffect(() => {
    //Check to see rfi and vendor questionnaire exists
    if (rfi && rfi.questionnaire) {
      const unansweredRequiredElements = filter(
        rfi.questionnaire,
        (element: any) => {
          if (
            element.requiredResponse &&
            !(
              element.fieldType === fieldTypes.SECTIONHEADER ||
              element.fieldType === fieldTypes.GUIDANCETEXT
            ) &&
            ((element.fieldType !== fieldTypes.ATTACHMENT &&
              (!element.answers ||
                element.answers.length <= 0 ||
                !element.answers[0].value)) ||
              (element.fieldType === fieldTypes.ATTACHMENT &&
                element.answers &&
                element.answers.length <= 0))
          ) {
            return true;
          }
          return false;
        }
      );

      var sortedUnansweredRequiredElements = sortBy(
        unansweredRequiredElements,
        (element: any) => {
          return element.sections.find(
            (x: SectionGroup) => x.sectionGroupName === props.section
          )?.sequence;
        }
      );

      setRequiredQuestions(sortedUnansweredRequiredElements);
    }
  }, [rfi.rfi]);

  //setting validates pass and fail states
  useEffect(() => {
    if (requiredVendorInfo.length > 0 || requiredQuestions.length > 0) {
      setValidationFail(true);
      setValidationPass(false);
    } else {
      setValidationFail(false);
      setValidationPass(true);
    }
  });

  useEffect(() => {
    if (vendorResponse.vendorResponse?.dateSubmitted) {
      setChecked(true);
      setSubmissionDate(
        dayjs
          .utc(vendorResponse.vendorResponse.dateSubmitted)
          .tz(timeZone ?? "America/New_York")
          .format("lll z")
      );
    }
  }, [vendorResponse.vendorResponse?.dateSubmitted]);

  //When the Responses Due Date has passed, the submit button will no longer be available on in progress vendor responses.
  useEffect(() => {
    if (rfi.rfi?.responsesDueDate) {
      const responseDatePassed = rfi.rfi?.responsesDueDate
        ? dayjs.utc(rfi.rfi?.responsesDueDate) <= dayjs.utc()
          ? true
          : false
        : true;

      if (responseDatePassed) {
        setResponseDueDatePassed(true);
      } else {
        setResponseDueDatePassed(false);
      }
    }
  }, [rfi]);

  //string manipulation based on state and props
  if (rfi.rfi && rfi.rfi.rfiNumber) {
    attestation_text = attestation_text.replace(
      "<RFI number>",
      rfi.rfi?.rfiNumber
    );
    if (VENDOR_NAME && VENDOR_NAME.answers && VENDOR_NAME.answers[0]?.value) {
      attestation_text = attestation_text.replace(
        "<Vendor Legal Business Name>",
        VENDOR_NAME.answers[0].value
      );
    } else {
      attestation_text = attestation_text.replace(
        "<Vendor Legal Business Name>",
        "my company"
      );
    }

    submission_text = submission_text.replace(
      "<RFI Number>",
      rfi.rfi?.rfiNumber
    );
    submission_text = submission_text.replace(
      "<date and time>",
      submissionDate
    );
  }

  // Render

  // Response Invalid
  if (rfi.rfi?.status === RfiStatus.CANCELLED) {
    return (
      <div>
        <h1>Vendor Response Validation</h1>
        <p className="guidance-text">
          This RFI is cancelled and will no longer be taking submissions.
        </p>
      </div>
    );
  }
  // Response Valid - Submitted
  if (vendorResponse.vendorResponse?.dateSubmitted) {
    return (
      <div>
        <h1>Vendor Submission</h1>
        <p className="guidance-text">{submission_text}</p>
        {props.user &&
          props.user &&
          props.user.isAuthenticated &&
          (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 && (
            <div id="wrapperDiv" className="guidance-text">
              <div id="div1" style={{ float: "left", marginRight: "10px" }}>
                <p className="guidance-text">
                  Click Print Response to download a copy for your records.
                </p>
              </div>
            </div>
          )}
      </div>
    );
  }

  // Response Valid - Not Submitted
  return (
    <div className="rfi-content">
      <h1>Vendor Response Validation</h1>

      {props.readOnly && (
        <p className="guidance-text">
          This RFI response cannot be submitted as the RFI has passed its
          Responses Due date.
        </p>
      )}

      {validationFail && !props.readOnly && (
        <p className="guidance-text">
          Fields displayed here require a value before the response can be
          submitted.
          <br></br>
          Once a value has been entered, click the Validate button to
          re-validate the response.
        </p>
      )}

      {validationPass === true && !props.readOnly && (
        <div>
          <p className="guidance-text">The response passed validations.</p>
        </div>
      )}

      {requiredVendorInfo.length > 0 && (
        <h2>
          <u>Vendor Information</u>
        </h2>
      )}
      {requiredVendorInfo.length > 0 &&
        requiredVendorInfo.map((element: any, i: number) => {
          let questionNumber = element.sections.find(
            (x: SectionGroup) => x.sectionGroupName === sectionNames.VENDOR_INFO
          )?.questionNumber;
          if (!props.user.isAuthenticated || props.readOnly) {
            return (
              <ReadOnlyElementContainer
                key={i}
                element={element}
                questionNumber={questionNumber}
                readOnly={props.readOnly}
              />
            );
          } else {
            return (
              <ElementContainer
                key={i}
                element={element}
                questionNumber={questionNumber}
                readOnly={props.readOnly}
              />
            );
          }
        })}

      {requiredQuestions.length > 0 && (
        <h2>
          <u>Questionnaire</u>
        </h2>
      )}
      {requiredQuestions.length > 0 &&
        requiredQuestions.map((element: any, i: number) => {
          let questionNumber = element.sections.find(
            (x: SectionGroup) =>
              x.sectionGroupName === sectionNames.QUESTIONNAIRE
          )?.questionNumber;
          if (
            !props.user.isAuthenticated ||
            props.readOnly ||
            vendorResponse.vendorResponse?.dateSubmitted
          ) {
            return (
              <ReadOnlyElementContainer
                key={i}
                element={element}
                questionNumber={questionNumber}
                readOnly={props.readOnly}
              />
            );
          } else {
            return (
              <ElementContainer
                key={i}
                element={element}
                questionNumber={questionNumber}
                readOnly={props.readOnly}
              />
            );
          }
        })}

      {validationPass === true && !props.readOnly && (
        <div className=" d-flex flex-nowrap" style={{ marginBottom: "50px" }}>
          <Checkbox
            id="attestation-check"
            className="k-mr-2"
            checked={checked}
            onChange={handleChange}
            autoFocus={true}
            readOnly={
              vendorResponse.vendorResponse?.dateSubmitted ||
              rfi.rfi?.status !== RfiStatus.POSTED
                ? true
                : false || props.readOnly
            }
          />
          <label htmlFor="attestation-check">{attestation_text}</label>
        </div>
      )}

      <div style={{ display: "inline-block" }}>
        {validationFail && !props.readOnly && (
          <div className="mt-5">
            <button
              className="btn btn-primary m-1"
              role="button"
              onClick={async () => {
                await onValidate();
              }}
            >
              <i className="fa-solid"> </i>
              Validate
            </button>
          </div>
        )}
        {validationPass === true &&
          !responseDueDatePassed &&
          checked &&
          !props.readOnly &&
          rfi.rfi?.status === RfiStatus.POSTED && (
            <button
              className="btn btn-primary m-1"
              role="button"
              onClick={() => {
                clickSubmit();
              }}
            >
              Submit Response
            </button>
          )}
        {props.user.isAuthenticated && props.setSelectedPage && (
          <div className="mt-5">
            <button
              className="btn btn-primary m-1"
              role="button"
              onClick={() => props.setSelectedPage(vendorNavPages.ATTACHMENTS)}
            >
              <i className="fa-solid fa-chevron-left"></i> Step 3: Additional
              Attachments
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorRfiValidation;
